import React, { useState, useEffect, useMemo } from 'react';

// Tickets Ada logged when it couldn't fulfil a real user ask. Surfaced
// here so the founder can see real product gaps without having to read
// every chat transcript.

type Status = 'open' | 'planned' | 'shipped' | 'dismissed';

interface FeatureRequest {
  id: number;
  user_id: number;
  project_id: number | null;
  conversation_id: number | null;
  user_request: string;
  reason: string | null;
  status: Status;
  admin_notes: string | null;
  created_at: string;
  resolved_at: string | null;
  resolved_by_user_id: number | null;
  user_email: string | null;
  user_first_name: string | null;
  user_last_name: string | null;
  project_name: string | null;
}

const STATUSES: Status[] = ['open', 'planned', 'shipped', 'dismissed'];

const STATUS_LABEL: Record<Status, string> = {
  open: 'Open',
  planned: 'Planned',
  shipped: 'Shipped',
  dismissed: 'Dismissed',
};

const STATUS_PILL: Record<Status, string> = {
  open: 'bg-amber-100 text-amber-800 border-amber-200',
  planned: 'bg-blue-100 text-blue-800 border-blue-200',
  shipped: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  dismissed: 'bg-gray-100 text-gray-600 border-gray-200',
};

const getToken = () => localStorage.getItem('token');
const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${getToken()}`,
  'Content-Type': 'application/json',
});

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function displayName(r: FeatureRequest): string {
  const parts = [r.user_first_name, r.user_last_name].filter(Boolean).join(' ').trim();
  return parts || r.user_email || `user #${r.user_id}`;
}

const AdaFeatureRequestsManager: React.FC = () => {
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('open');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [draftNotes, setDraftNotes] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
      const r = await fetch(`/api/admin/ada-feature-requests${qs}`, { headers: authHeaders() });
      if (!r.ok) {
        const text = await r.text().catch(() => '');
        throw new Error(text || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setRequests(data.requests || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load feature requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const counts = useMemo(() => {
    // Counts only reflect the currently-loaded list; for the global
    // status pills we want all statuses' counts. Keep it simple — the
    // filter pills show counts for the current view only when relevant.
    const c: Record<Status, number> = { open: 0, planned: 0, shipped: 0, dismissed: 0 };
    for (const r of requests) c[r.status]++;
    return c;
  }, [requests]);

  const updateRequest = async (id: number, patch: Partial<{ status: Status; admin_notes: string }>) => {
    setSavingId(id);
    try {
      const r = await fetch(`/api/admin/ada-feature-requests/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(patch),
      });
      if (!r.ok) {
        const text = await r.text().catch(() => '');
        throw new Error(text || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setRequests(prev => prev.map(rq => (rq.id === id ? { ...rq, ...data.request } : rq)));
    } catch (e: any) {
      setError(e?.message || 'Failed to update');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Ada feature requests</h2>
          <p className="text-sm text-gray-600 mt-1">
            Things Ada logged when it couldn't fulfil a real user ask. Triage from open → planned → shipped (or dismissed).
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-xs px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Status filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {(['all', ...STATUSES] as const).map(s => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`text-xs px-3 py-1 rounded-full border transition ${
              statusFilter === s
                ? 'bg-purple-600 text-white border-purple-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {s === 'all' ? 'All' : STATUS_LABEL[s]}
            {statusFilter === s && requests.length > 0 && (
              <span className="ml-1.5 opacity-80">{requests.length}</span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-xs underline">Dismiss</button>
        </div>
      )}

      {!loading && requests.length === 0 && !error && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-sm text-gray-500">
          {statusFilter === 'all'
            ? 'No feature requests logged yet.'
            : `No ${STATUS_LABEL[statusFilter as Status].toLowerCase()} feature requests.`}
        </div>
      )}

      <div className="space-y-2">
        {requests.map(r => {
          const isOpen = expandedId === r.id;
          const draft = draftNotes[r.id] ?? r.admin_notes ?? '';
          const draftDirty = draft !== (r.admin_notes ?? '');
          return (
            <div key={r.id} className="border border-gray-200 rounded-lg bg-white">
              <button
                type="button"
                onClick={() => setExpandedId(isOpen ? null : r.id)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 transition"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {r.user_request}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                      <span>{displayName(r)}</span>
                      {r.project_name && <span>· {r.project_name}</span>}
                      <span>· {formatDate(r.created_at)}</span>
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${STATUS_PILL[r.status]} flex-shrink-0`}>
                    {STATUS_LABEL[r.status]}
                  </span>
                  <span className="text-gray-400 text-xs flex-shrink-0">{isOpen ? '▾' : '▸'}</span>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-gray-100 px-4 py-3 space-y-3 bg-gray-50/50">
                  {r.reason && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Why Ada couldn't fulfil it</div>
                      <div className="text-sm text-gray-700">{r.reason}</div>
                    </div>
                  )}

                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Status</div>
                    <div className="flex items-center gap-2">
                      {STATUSES.map(s => (
                        <button
                          key={s}
                          type="button"
                          disabled={savingId === r.id || s === r.status}
                          onClick={() => updateRequest(r.id, { status: s })}
                          className={`text-xs px-3 py-1 rounded border transition ${
                            s === r.status
                              ? `${STATUS_PILL[s]} cursor-default`
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                          } disabled:opacity-50`}
                        >
                          {STATUS_LABEL[s]}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Admin notes</div>
                    <textarea
                      value={draft}
                      onChange={(e) => setDraftNotes(prev => ({ ...prev, [r.id]: e.target.value }))}
                      placeholder="Triage notes, links to issues, etc."
                      rows={3}
                      className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-400"
                    />
                    {draftDirty && (
                      <div className="mt-1 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setDraftNotes(prev => ({ ...prev, [r.id]: r.admin_notes ?? '' }))}
                          className="text-xs px-2 py-1 text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={savingId === r.id}
                          onClick={() => updateRequest(r.id, { admin_notes: draft })}
                          className="text-xs px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                        >
                          {savingId === r.id ? 'Saving…' : 'Save notes'}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="text-[10px] text-gray-400 flex items-center gap-3">
                    <span>logged {formatDate(r.created_at)}</span>
                    {r.resolved_at && <span>· resolved {formatDate(r.resolved_at)}</span>}
                    {r.user_email && <span>· {r.user_email}</span>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdaFeatureRequestsManager;
